import React, {
  ChangeEvent,
  DragEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Menu } from "@mui/material";
import MUIMenuItem from "@mui/material/MenuItem";

import Box from "veneer/Box";
import Button from "veneer/Button";
import Checkbox from "veneer/Checkbox";
import { IconButton } from "veneer/IconButton";
import Stack from "veneer/Stack";
import { DragIndicatorIcon, ViewColumnIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { createDataTestId } from "vistar/ui/test_helpers/dataTestId";

export interface TableSettingsColumn {
  id: string;
  label: string;
  locked: boolean;
}

export interface TableSettingsValue {
  hiddenColumnIds: ReadonlyArray<string>;
  columnOrder: ReadonlyArray<string>;
}

export interface TableSettingsProps {
  columns: ReadonlyArray<TableSettingsColumn>;
  dataTestId?: string;
  hiddenColumnIds: ReadonlyArray<string>;
  columnOrder: ReadonlyArray<string>;
  onHiddenColumnIdsChange: (ids: ReadonlyArray<string>) => void;
  onColumnOrderChange: (order: ReadonlyArray<string>) => void;
  onReset: () => void;
}

interface ColumnRowProps {
  checked: boolean;
  isDragging: boolean;
  onToggle: (id: string) => void;
  onDragStart: (event: DragEvent, id: string) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent) => void;
  onDragEnter: (id: string) => void;
  onDrop: (event: DragEvent) => void;
  option: TableSettingsColumn;
  selectionLocked: boolean;
}

interface PartitionedColumns {
  visibleColumns: TableSettingsColumn[];
  hiddenColumns: TableSettingsColumn[];
}

function stopMenuEvent(event: {
  preventDefault: () => void;
  stopPropagation: () => void;
}) {
  event.preventDefault();
  event.stopPropagation();
}

function handleDragOver(event: DragEvent) {
  stopMenuEvent(event);
  event.dataTransfer.dropEffect = "move";
}

function ColumnRow({
  checked,
  isDragging,
  onToggle,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDrop,
  option,
  selectionLocked,
}: ColumnRowProps) {
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    stopMenuEvent(event);
    if (!selectionLocked) {
      onToggle(option.id);
    }
  };

  return (
    <DragMenuItem
      data-testid={createDataTestId(["column-row", option.id])}
      isDragging={isDragging}
      isLocked={selectionLocked}
      onDragEnter={() => onDragEnter(option.id)}
      onDragOver={onDragOver}
      onDragOverCapture={() => onDragEnter(option.id)}
      onDrop={onDrop}
    >
      <FullWidthMenuItem disableRipple={selectionLocked} onClick={handleClick}>
        <Box display="flex" justifyContent="center" minWidth={32} mr={1}>
          <Checkbox
            checked={checked}
            dataTestId={"column-row-checkbox"}
            disabled={selectionLocked}
            onChange={(_, event) => {
              event.stopPropagation();
            }}
          />
        </Box>
        <Typography component="span" flex={1}>
          {option.label}
        </Typography>
        {checked && (
          <DragHandle
            data-testid="drag-handle"
            draggable
            onClick={(event: MouseEvent<HTMLSpanElement>) => {
              event.stopPropagation();
            }}
            onDragEnd={onDragEnd}
            onDragStart={(event: DragEvent) => onDragStart(event, option.id)}
          >
            <DragIndicatorIcon />
          </DragHandle>
        )}
      </FullWidthMenuItem>
    </DragMenuItem>
  );
}

export default function TableSettings({
  columns,
  dataTestId,
  hiddenColumnIds,
  columnOrder,
  onHiddenColumnIdsChange,
  onColumnOrderChange,
  onReset,
}: TableSettingsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [localOrder, setLocalOrder] =
    useState<ReadonlyArray<string>>(columnOrder);

  const isDraggingRef = useRef(false);
  const lastOverIdRef = useRef<string | null>(null);
  const pendingOrderRef = useRef<ReadonlyArray<string> | null>(null);

  const open = Boolean(anchorEl);

  const hiddenSet = useMemo(() => new Set(hiddenColumnIds), [hiddenColumnIds]);
  const lockedSet = useMemo(
    () => new Set(columns.filter((c) => c.locked).map((c) => c.id)),
    [columns],
  );

  const displayOrder = draggingId ? localOrder : columnOrder;

  const orderedColumns = useMemo(
    () => getOrderedColumns(columns, displayOrder),
    [columns, displayOrder],
  );

  // Keep localOrder in sync with the prop when not dragging so that external
  // order updates (e.g. parent persisting a new order) are reflected after any
  // drag completes.
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalOrder(columnOrder);
    }
  }, [columnOrder]);

  const { visibleColumns, hiddenColumns } = useMemo(
    () => partitionColumnsByVisibility(orderedColumns, hiddenSet),
    [orderedColumns, hiddenSet],
  );

  const sortedHiddenColumns = useMemo(
    () => sortColumnsByLabel(hiddenColumns),
    [hiddenColumns],
  );

  const selectableIds = useMemo(
    () => columns.filter((c) => !c.locked).map((c) => c.id),
    [columns],
  );

  const allSelected = useMemo(
    () => selectableIds.every((id) => !hiddenSet.has(id)),
    [selectableIds, hiddenSet],
  );

  const partiallySelected =
    !allSelected && selectableIds.some((id) => !hiddenSet.has(id));
  const showAvailableColumns =
    visibleColumns.length > 0 && sortedHiddenColumns.length > 0;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = useCallback(
    (id: string) => {
      if (isDraggingRef.current || lockedSet.has(id)) {
        return;
      }

      const isVisible = !hiddenSet.has(id);

      if (isVisible) {
        onColumnOrderChange(moveIdsToEnd(columnOrder, [id]));
        onHiddenColumnIdsChange([...hiddenColumnIds, id]);
      } else {
        onHiddenColumnIdsChange(hiddenColumnIds.filter((hid) => hid !== id));
      }
    },
    [
      lockedSet,
      hiddenSet,
      hiddenColumnIds,
      columnOrder,
      onHiddenColumnIdsChange,
      onColumnOrderChange,
    ],
  );

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      const toHide = selectableIds.filter((id) => !hiddenSet.has(id));
      onColumnOrderChange(moveIdsToEnd(columnOrder, toHide));
      onHiddenColumnIdsChange([...hiddenColumnIds, ...toHide]);
    } else {
      const toShowSet = new Set(
        selectableIds.filter((id) => hiddenSet.has(id)),
      );
      onHiddenColumnIdsChange(
        hiddenColumnIds.filter((id) => !toShowSet.has(id)),
      );
    }
  }, [
    allSelected,
    selectableIds,
    hiddenSet,
    hiddenColumnIds,
    columnOrder,
    onHiddenColumnIdsChange,
    onColumnOrderChange,
  ]);

  const handleDragStart = useCallback(
    (event: DragEvent, id: string) => {
      if (hiddenSet.has(id)) {
        return;
      }

      isDraggingRef.current = true;
      pendingOrderRef.current = null;
      setLocalOrder(columnOrder);
      setDraggingId(id);
      lastOverIdRef.current = null;
      event.dataTransfer.setData("text/plain", id);
      event.dataTransfer.effectAllowed = "move";
      event.stopPropagation();

      if (event.currentTarget instanceof HTMLElement) {
        event.dataTransfer.setDragImage(event.currentTarget, 10, 10);
      }
    },
    [hiddenSet, columnOrder],
  );

  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    pendingOrderRef.current = null;
    setDraggingId(null);
    setLocalOrder(columnOrder);
    lastOverIdRef.current = null;
  }, [columnOrder]);

  const handleDragEnter = useCallback(
    (overId: string) => {
      if (!draggingId || draggingId === overId) {
        return;
      }

      if (hiddenSet.has(draggingId) || hiddenSet.has(overId)) {
        return;
      }

      if (lastOverIdRef.current === overId) {
        return;
      }

      lastOverIdRef.current = overId;

      const nextOrder = getReorderedVisibleColumnIds({
        visibleColumns,
        hiddenColumns,
        draggingId,
        overId,
      });

      if (!nextOrder) {
        return;
      }

      pendingOrderRef.current = nextOrder;
      setLocalOrder(nextOrder);
    },
    [draggingId, hiddenSet, visibleColumns, hiddenColumns],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      stopMenuEvent(event);
      if (pendingOrderRef.current) {
        onColumnOrderChange(pendingOrderRef.current);
      }
      handleDragEnd();
    },
    [handleDragEnd, onColumnOrderChange],
  );

  const handleToggleAllClick = (event: MouseEvent<HTMLDivElement>) => {
    stopMenuEvent(event);
    if (selectableIds.length > 0) {
      handleToggleAll();
    }
  };

  const handleToggleAllChange = (
    _checked: boolean,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    stopMenuEvent(event);
    handleToggleAll();
  };

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    stopMenuEvent(event);
    onReset();
  };

  const renderColumnRow = (
    option: TableSettingsColumn,
    checked: boolean,
    isDragging = false,
  ) => (
    <ColumnRow
      checked={checked}
      isDragging={isDragging}
      key={option.id}
      onDragEnd={handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
      onToggle={handleToggle}
      option={option}
      selectionLocked={lockedSet.has(option.id)}
    />
  );

  return (
    <>
      <IconButton
        color="inherit"
        dataTestId={dataTestId}
        icon={<ViewColumnIcon />}
        onClick={handleOpen}
        title="Manage Columns"
      />

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        keepMounted
        MenuListProps={{
          disablePadding: true,
          onClick: (event) => {
            event.stopPropagation();
          },
        }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <ButtonWrapper>
          <Stack direction="row" justifyContent="space-between" px={2} py={1}>
            <Stack
              alignItems="center"
              dataTestId="show-hide-all-button"
              direction="row"
              onClick={handleToggleAllClick}
            >
              <Checkbox
                checked={allSelected}
                disabled={selectableIds.length === 0}
                indeterminate={partiallySelected}
                onChange={handleToggleAllChange}
              />
              <Typography component="span">Show/Hide all</Typography>
            </Stack>
            <Button
              dataTestId="reset-button"
              onClick={handleReset}
              variant="text"
            >
              Reset
            </Button>
          </Stack>
        </ButtonWrapper>
        <ListItemsWrapper>
          {visibleColumns.map((option) =>
            renderColumnRow(option, true, draggingId === option.id),
          )}
          {showAvailableColumns &&
            sortedHiddenColumns.map((option) => renderColumnRow(option, false))}
        </ListItemsWrapper>
      </Menu>
    </>
  );
}

const DragHandle = styled("span")(({ theme }) => ({
  alignItems: "center",
  cursor: "grab",
  display: "inline-flex",
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.25),
  userSelect: "none",
}));

const ButtonWrapper = styled(Box)({
  backgroundColor: Colors.white,
  borderBottom: `1px solid ${Colors.gray200}`,
  minWidth: 244,
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const DragMenuItem = styled("div", {
  shouldForwardProp: (prop: string) =>
    prop !== "isDragging" && prop !== "isLocked",
})<{ isDragging: boolean; isLocked: boolean }>(
  ({ isDragging = false, isLocked = false }) => ({
    backgroundColor: isDragging ? Colors.blue100 : "transparent",
    // isDragging takes precedence so the grab cursor still shows during drag
    cursor: isDragging ? "grabbing" : isLocked ? "default" : "pointer",
    display: "flex",
    opacity: isDragging ? 0.5 : 1,
  }),
);

const ListItemsWrapper = styled(Box)({
  flex: "1 1 auto",
});

const FullWidthMenuItem = styled(MUIMenuItem)({
  width: "100%",
});

export function arrayMove<T>(
  items: ReadonlyArray<T>,
  from: number,
  to: number,
): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function getOrderedColumns(
  columns: ReadonlyArray<TableSettingsColumn>,
  columnOrder: ReadonlyArray<string>,
): TableSettingsColumn[] {
  const columnById = new Map(columns.map((column) => [column.id, column]));
  const orderedColumns = columnOrder
    .map((id) => columnById.get(id))
    .filter(
      (column): column is TableSettingsColumn =>
        column !== null && column !== undefined,
    );
  const orderedColumnIds = new Set(orderedColumns.map((column) => column.id));
  const remainingColumns = columns.filter(
    (column) => !orderedColumnIds.has(column.id),
  );
  return [...orderedColumns, ...remainingColumns];
}

function partitionColumnsByVisibility(
  columns: ReadonlyArray<TableSettingsColumn>,
  hiddenColumnIds: ReadonlySet<string>,
): PartitionedColumns {
  return {
    visibleColumns: columns.filter((c) => !hiddenColumnIds.has(c.id)),
    hiddenColumns: columns.filter((c) => hiddenColumnIds.has(c.id)),
  };
}

function sortColumnsByLabel(
  columns: ReadonlyArray<TableSettingsColumn>,
): TableSettingsColumn[] {
  return [...columns].sort((a, b) => a.label.localeCompare(b.label));
}

interface ReorderVisibleColumnIdsOptions {
  visibleColumns: ReadonlyArray<TableSettingsColumn>;
  hiddenColumns: ReadonlyArray<TableSettingsColumn>;
  draggingId: string;
  overId: string;
}

function getReorderedVisibleColumnIds({
  visibleColumns,
  hiddenColumns,
  draggingId,
  overId,
}: ReorderVisibleColumnIdsOptions): ReadonlyArray<string> | null {
  const fromIndex = visibleColumns.findIndex((c) => c.id === draggingId);
  const toIndex = visibleColumns.findIndex((c) => c.id === overId);

  if (fromIndex === -1 || toIndex === -1) {
    return null;
  }

  return [
    ...arrayMove(visibleColumns, fromIndex, toIndex),
    ...hiddenColumns,
  ].map((c) => c.id);
}

function moveIdsToEnd(
  order: ReadonlyArray<string>,
  ids: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const moving = new Set(ids);
  return [...order.filter((id) => !moving.has(id)), ...ids];
}
